<script setup lang="ts">
import type LxcSideCanvas from "@lxc/app-device-common/src/components/LxcSideCanvas.vue";
import { ref, type Ref } from "vue";
import type LxcTabsWithNavigation from "~/core/components/TabsWithNavigation.vue";
import type { TabNavigation } from "~/core/components/TabsWithNavigation.vue";
import { SearchMode } from "~/core/composables/useSearch";
import {
  PATH_BUSINESS_UNITS_ID_SETTINGS_USERS_LOGIN_DELEGATIONS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USERS_LOGIN_INFORMATION,
} from "~/modules/businessUnit/constants";
import {
  PATH_ORGANIZATIONS_ID_SETTINGS_USERS_LOGIN_DELEGATION,
  PATH_ORGANIZATIONS_ID_SETTINGS_USERS_LOGIN_INFORMATION,
} from "~/modules/organization/constants";
import { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import { useFetchReceivedDelegations } from "~/modules/relation/delegation/composables/useFetchReceivedDelegations";
import { useFetchUserDelegations } from "~/modules/relation/delegation/composables/useFetchUserDelegations";
import type UserInformationForm from "~/modules/user/components/UserInformationForm.vue";
import { useFetchProfiles } from "~/modules/user/composables/useFetchProfiles";
import { useGetUser } from "~/modules/user/composables/useGetUser";
import { UserSessionEntityType } from "~/modules/user/models/UserSessionEntityType.enum";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import { isProfileAuthorized } from "~/modules/user/utils/isProfileAuthorized";

const props = defineProps<{
  show: boolean;
  businessUnitCode?: number;
  organizationCode?: number;
  organizationType?: OrganizationType;
  userLogin?: string;
  useExternalIdp?: boolean;
}>();

const { userSession } = useUserSession();
const { t } = useI18n();
const router = useRouter();

const emit = defineEmits(["update:show", "close"]);

const showCanvas: WritableComputedRef<boolean> = computed({
  get: () => {
    return props.show;
  },
  set: (shown: boolean) => {
    emit("update:show", shown);
  },
});

const {
  results: profileResults,
  fetchData: profileFetchAll,
  isLoading: isLoadingProfiles,
} = useFetchProfiles({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
  searchMode: SearchMode.FILTER_SEARCH,
});

const {
  getUser,
  isLoading: isLoadingGetUser,
  error: errorGetUser,
  result: resultGetUser,
} = useGetUser({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});

// Separate instance of useGetUser composable to get the current user with its own context
const currentUserContext = computed(() => {
  if (!userSession) {
    return {};
  }

  switch (userSession.entity.type) {
    case UserSessionEntityType.OPERATOR:
    case UserSessionEntityType.ADMINISTRATION:
      return { organizationCode: userSession.entity.code };
    case UserSessionEntityType.BUSINESS_UNIT:
      return { businessUnitCode: userSession.entity.code };
    default:
      return {};
  }
});

const {
  getUser: getCurrentUserDetails,
  isLoading: isLoadingGetCurrentUser,
  error: errorGetCurrentUser,
  result: currentUser,
} = useGetUser(currentUserContext.value);

const {
  fetchAllData: fetchAllReceivedDelegations,
  allResults: receivedDelegations,
  isLoading: isLoadingReceivedDelegations,
} = useFetchReceivedDelegations({
  organizationCode: props.organizationCode,
  businessUnitCode: props.businessUnitCode,
});

const {
  fetchAllData: fetchAllUserDelegations,
  allResults: userReceivedDelegations,
  isLoading: isLoadingUserDelegations,
} = useFetchUserDelegations({
  organizationCode: props.organizationCode,
  businessUnitCode: props.businessUnitCode,
});

/**
 * Can manage users delegations if:
 *   - the current user is not a SUPER_ADMINISTRATION user
 *   - managing users of business unit or administration organization
 */
const canManageUsersDelegations = computed(
  () =>
    userSession?.entity.type !== UserSessionEntityType.SUPER_ADMINISTRATION &&
    (!!props.businessUnitCode ||
      (!!props.organizationCode &&
        props.organizationType === OrganizationType.ADMINISTRATION)),
);

const canUpdateUser = computed(() => {
  if (
    !profileResults.value ||
    !currentUser.value ||
    (props.userLogin && !resultGetUser.value)
  ) {
    return false;
  }
  if (
    props.userLogin &&
    resultGetUser.value &&
    resultGetUser.value.profile.id !== currentUser.value.profile.id
  ) {
    return !!isProfileAuthorized(
      profileResults.value,
      resultGetUser.value.profile.id,
    );
  }
  return true;
});

/** Tabs configuration */
const tabsWithNavigationRef: Ref<typeof LxcTabsWithNavigation | undefined> =
  ref();
const informationTabId = "information";
const delegationsTabId = "delegations";

const informationTabRedirectPath = computed(() => {
  if (!props.userLogin) {
    return "";
  }

  if (props.businessUnitCode) {
    return router.resolve({
      name: PATH_BUSINESS_UNITS_ID_SETTINGS_USERS_LOGIN_INFORMATION,
      params: {
        id: props.businessUnitCode,
        userLogin: props.userLogin,
      },
    }).href;
  }

  if (
    props.organizationCode &&
    props.organizationType === OrganizationType.ADMINISTRATION
  ) {
    return router.resolve({
      name: PATH_ORGANIZATIONS_ID_SETTINGS_USERS_LOGIN_INFORMATION,
      params: {
        id: props.organizationCode,
      },
    }).href;
  }

  return "";
});
const delegationsTabRedirectPath = computed(() => {
  if (!props.userLogin) {
    return "";
  }

  if (props.businessUnitCode) {
    return router.resolve({
      name: PATH_BUSINESS_UNITS_ID_SETTINGS_USERS_LOGIN_DELEGATIONS,
      params: {
        id: props.businessUnitCode,
        userLogin: props.userLogin,
      },
    }).href;
  }

  if (
    props.organizationCode &&
    props.organizationType === OrganizationType.ADMINISTRATION
  ) {
    return router.resolve({
      name: PATH_ORGANIZATIONS_ID_SETTINGS_USERS_LOGIN_DELEGATION,
      params: {
        id: props.organizationCode,
      },
    }).href;
  }

  return "";
});

const selectedTabIndex: Ref<number | undefined> = ref();

const tabNavigations: ComputedRef<TabNavigation[]> = computed(() => {
  const tabNavigations: TabNavigation[] = [
    {
      id: informationTabId,
      redirectPath: informationTabRedirectPath.value,
      labelTranslationKey: t("user.form.tabs.information"),
    },
  ];
  if (
    props.userLogin &&
    canManageUsersDelegations.value &&
    receivedDelegations?.value &&
    receivedDelegations.value.length > 0
  ) {
    tabNavigations.push({
      id: delegationsTabId,
      redirectPath: delegationsTabRedirectPath.value,
      labelTranslationKey: t("user.form.tabs.delegations"),
    });
  }
  return tabNavigations;
});
/** Ends Tabs configuration */

const userInformationFormRef = ref<InstanceType<typeof UserInformationForm>>();
const isLoadingCreate = computed(
  () => userInformationFormRef.value?.isLoadingCreate,
);
const isLoadingUpdate = computed(
  () => userInformationFormRef.value?.isLoadingUpdate,
);
const formHasChanged = computed(
  () => userInformationFormRef.value?.formHasChanged,
);
const isFormValid = computed(() => userInformationFormRef.value?.isFormValid);
const edited = ref(false);

function clearForm() {
  userInformationFormRef.value?.clearForm();
}
function onSubmit() {
  userInformationFormRef.value?.onSubmit();
}

const sideCanvasHeaderTitle: ComputedRef<string> = computed(() => {
  // Creating a new user
  if (!props.userLogin) {
    return t("user.create.title");
  }

  // Loading user data
  if (isLoadingGetUser.value || isLoadingGetCurrentUser.value) {
    return "";
  }

  // Updating or reading existing user
  return canUpdateUser.value ? t("user.update.title") : t("user.read.title");
});

const labelButtonSave = computed(() =>
  props.userLogin ? t("button.save") : t("user.create.saveButton"),
);

const sideCanvasRef: Ref<typeof LxcSideCanvas | undefined> = ref();

function onSuccess() {
  onClose();
}

function onClose() {
  showCanvas.value = false;
  clearForm();
  emit("close", {
    edited: edited.value,
  });
  edited.value = false;
  resultGetUser.value = undefined;
  errorGetUser.value = undefined;
}

function onCancelClick() {
  sideCanvasRef.value?.hide();
}

onMounted(async () => {
  profileFetchAll();
  if (userSession?.login) {
    await getCurrentUserDetails(userSession.login);
  }
  if (canManageUsersDelegations.value && fetchAllReceivedDelegations) {
    await fetchAllReceivedDelegations();
  }
});

watch(
  () => props.userLogin,
  async (newUserLogin) => {
    if (newUserLogin) {
      await getUser(newUserLogin);
      if (canManageUsersDelegations.value) {
        await fetchAllUserDelegations(new Map([["userLogin", newUserLogin]]));
      }
    }
  },
  {
    immediate: true,
  },
);

defineExpose({
  currentUser,
  isLoadingGetCurrentUser,
  errorGetCurrentUser,
  profileResults,
});
</script>

<template>
  <lxc-side-canvas
    ref="sideCanvasRef"
    v-model:show="showCanvas"
    :header="sideCanvasHeaderTitle"
    size="1/2"
    :confirm-title="
      userLogin
        ? t('user.update.confirm.title')
        : t('user.create.confirm.title')
    "
    :confirm-message="t('user.confirm.message')"
    :confirm-ok-label="t('button.confirm')"
    :confirm-cancel-label="t('button.cancel')"
    :confirm-enabled="formHasChanged"
    body-class="!py-5"
    @hidden="onClose"
  >
    <container-component
      :py="0"
      :px="0"
      :is-loading="
        isLoadingGetCurrentUser ||
        isLoadingGetUser ||
        isLoadingProfiles ||
        isLoadingReceivedDelegations ||
        isLoadingUserDelegations
      "
      :error="errorGetCurrentUser || errorGetUser"
    >
      <tabs-with-navigation
        ref="tabsWithNavigationRef"
        :key="userLogin"
        :tab-navigations="tabNavigations"
        @update:selected-tab-index="
          (newTabIndex) => (selectedTabIndex = newTabIndex)
        "
      >
        <template #[informationTabId]>
          <user-information-form
            ref="userInformationFormRef"
            v-model:edited="edited"
            :show="show"
            :business-unit-code="businessUnitCode"
            :organization-code="organizationCode"
            :organization-type="organizationType"
            :user-login="userLogin"
            :selected-user="resultGetUser"
            :current-user="currentUser"
            :use-external-idp="useExternalIdp"
            :profiles="profileResults"
            :can-update-user="canUpdateUser"
            @success="onSuccess"
          />
        </template>
        <template #[delegationsTabId]>
          <user-delegations
            :business-unit-code="businessUnitCode"
            :organization-code="organizationCode"
            :user-login="userLogin"
            :received-delegations="receivedDelegations"
            :user-delegations="userReceivedDelegations"
          />
        </template>
      </tabs-with-navigation>
    </container-component>

    <template #footer>
      <div class="flex gap-6">
        <lxc-button
          html-type="button"
          type="tertiary"
          :text="t('button.cancel')"
          :title="t('button.cancel')"
          @click="onCancelClick"
        />
        <lxc-button
          v-if="canUpdateUser"
          html-type="button"
          type="primary"
          class="grow"
          :text="labelButtonSave"
          :title="labelButtonSave"
          :is-loading="isLoadingCreate || isLoadingUpdate"
          :disabled="!isFormValid || !formHasChanged"
          @click="onSubmit"
        />
      </div>
    </template>
  </lxc-side-canvas>
</template>
